import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../utils/constants";

export default function AdminBannerEdit() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    order_number: 0,
    image: null,
    category: "home",
    isActive: true,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${CONSTANTS.baseRoute}/banners/${id}`);
        const banner = res.data;

        setFormData({
          name: banner.name || "",
          order_number: banner.order_number || 0,
          image: null,
          category: banner.category || "home",
          isActive: banner.is_active ?? true,
        });

        if (banner.image) {
          setImagePreview(`${CONSTANTS.baseRoute}${banner.image}`);
        }
      } catch (error) {
        console.error("Error fetching banner:", error);
        setMessage({
          type: "danger",
          text:
            t("adminBannerEdit.fetchError") ||
            "Banner maglumatlaryny alyp bolmady!",
        });
        setTimeout(() => navigate("/admin/home/banner"), 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchBanner();
  }, [id, navigate, t]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setMessage({
          type: "danger",
          text: t("adminBannerAdd.onlyImagesAllowed"),
        });
        return;
      }
      setFormData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
      setMessage({ type: "", text: "" });
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setMessage({ type: "danger", text: t("adminBannerAdd.nameRequired") });
      return false;
    }

    if (!imagePreview && !formData.image) {
      setMessage({ type: "danger", text: t("adminBannerAdd.imageRequired") });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setMessage({ type: "", text: "" });
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("order_number", formData.order_number);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("is_active", formData.isActive);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      await axios.put(`${CONSTANTS.baseRoute}/banners/${id}`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage({
        type: "success",
        text:
          t("adminBannerEdit.successMessage") ||
          "Banner üstünlikli üýtgedildi!",
      });

      setTimeout(() => navigate("/admin/home/banner"), 1500);
    } catch (error) {
      console.error("Error updating banner:", error);
      setMessage({
        type: "danger",
        text:
          error.response?.data?.message ||
          t("adminBannerEdit.submitError") ||
          "Banner üýtgetmekde näsazlyk ýüze çykdy.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-main p-2 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-main p-2">
      <div className="d-flex gap-2 align-items-center mb-3">
        <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="btn admin-icon-btn" onClick={() => navigate(+1)}>
          <i className="bi bi-chevron-right"></i>
        </button>
        <h5 className="fw-bold mb-0">
          {t("adminBannerEdit.title") || "Banner Üýtgetmek"}
        </h5>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} shadow-sm mb-4`} role="alert">
          {message.text}
        </div>
      )}

      <div className="card shadow-sm border-0 rounded">
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control rounded-3"
                    id="name"
                    placeholder={t("adminBannerAdd.namePlaceholder")}
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                  <label htmlFor="name">
                    {t("adminBannerAdd.name")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="number"
                    className="form-control rounded-3"
                    id="order_number"
                    placeholder={t("adminBannerAdd.orderPlaceholder")}
                    value={formData.order_number}
                    onChange={handleInputChange}
                    min="0"
                  />
                  <label htmlFor="order_number">
                    {t("adminBannerAdd.order")}
                  </label>
                </div>
              </div>

              <div className="col-12">
                <label htmlFor="image" className="form-label fw-semibold small">
                  {t("adminBannerAdd.image")}{" "}
                  <span className="text-danger">*</span>
                  <small className="text-muted ms-2">
                    ({t("adminBannerAdd.recommendedSize") || "847×423"})
                  </small>
                </label>

                <input
                  type="file"
                  className="form-control rounded-3"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                />

                {imagePreview && (
                  <div className="mt-3 overflow-hidden">
                    <img
                      src={imagePreview}
                      alt={t("adminBannerAdd.preview")}
                      className="img-fluid w-100"
                      style={{ maxHeight: "280px", objectFit: "contain" }}
                    />
                  </div>
                )}
              </div>

              <div className="col-md-6 d-flex align-items-center">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="isActive">
                    {t("adminBannerAdd.active")}
                  </label>
                </div>
              </div>

              <div className="col-12 d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  className="btn btn-primary px-5 py-2 fw-bold rounded-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t("adminBannerEdit.submitting") || "Täzelenýär..."}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>
                      {t("adminBannerEdit.submit") || "Ýatda sakla"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
